'use client';

import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MultiSelect } from '@/components/ui/multi-select';

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
};

export default function ContactForm({
    variant = 'card', // "default" | "card" | "glass"
    layout = 'vertical', // ✅ "vertical" | "horizontal"
    isLabel = false,
    className,
    contactHeader = true,
    title = 'Let’s talk',
    subtitle = 'Tell us about your project and we’ll get back within 24 hours.',
}) {
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        services: [],
        message: '',
    });
    const [loading, setLoading] = useState(false);

    const serviceOptions = [
        'Publishing',
        'Marketing',
        'Writing',
        'Trailer Service',
        'Editing Services',
        'Design Services',
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handlePhoneChange = (value) => {
        setForm((p) => ({ ...p, phone: value || '' }));
    };

    const handleServicesChange = (value) => {
        setForm((p) => ({ ...p, services: value }));
    };

    const router = useRouter();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { name, email, phone, services, message } = form;

        if (!name || !email || !phone || !message) {
            toast.error('Please fill in all required fields.');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            toast.error('Please enter a valid email address.');
            return;
        }
        if (typeof isValidPhoneNumber === 'function' && !isValidPhoneNumber(phone)) {
            toast.error('Please enter a valid phone number.');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch('/api/contact-form', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name,
                    email,
                    phone,
                    services: services.join(', '),
                    message,
                    agreeMarketing: null,
                    agreeTerms: null,
                    source: window.location.href,
                }),
            });
            const result = await res.json();

            if (res.ok) {
                toast.success('Your message has been sent successfully!');
                setForm({ name: '', email: '', phone: '', services: [], message: '' });
                router.push('/thank-you');
                return;
            } else {
                toast.error(result?.error || 'Something went wrong.');
            }
        } catch (err) {
            toast.error('Failed to submit form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const styles = useMemo(() => {
        const base = 'relative rounded-2xl border p-6 sm:p-8 shadow-sm';

        const card = 'bg-white border-border text-foreground';
        const glass =
            'bg-background/60 backdrop-blur-xl border-border/60 shadow-lg text-foreground';

        // ✅ NEW VARIANT
        const primaryCard = 'bg-primary/80 border-primary text-white shadow-lg';

        const isPrimary = variant === 'primaryCard';

        return {
            wrapper: cn(
                base,
                isPrimary ? primaryCard : variant === 'glass' ? glass : card,
                className
            ),

            // labels/hints
            label: cn('text-sm font-medium', isPrimary ? 'text-white' : ''),
            hint: cn('text-xs', isPrimary ? 'text-white/80' : 'text-muted-foreground'),

            // fields
            field: cn(
                'h-11 rounded-md px-4 text-sm shadow-none border focus-visible:ring-2',
                isPrimary
                    ? 'bg-white text-gray-900 border-white/30 placeholder:text-gray-400 focus-visible:ring-white/40 focus-visible:border-white'
                    : 'bg-background text-foreground border-input placeholder:text-muted-foreground focus-visible:ring-ring/40 focus-visible:border-ring'
            ),

            textarea: cn(
                'min-h-[120px] rounded-md px-4 py-3 text-sm border focus-visible:ring-2',
                isPrimary
                    ? 'bg-white text-gray-900 border-white/30 placeholder:text-gray-400 focus-visible:ring-white/40 focus-visible:border-white'
                    : 'bg-background text-foreground border-input placeholder:text-muted-foreground focus-visible:ring-ring/40 focus-visible:border-ring'
            ),

            phoneWrap: cn(
                'rounded-md border px-4 py-1 focus-within:ring-2',
                isPrimary
                    ? 'bg-white border-white/30 focus-within:ring-white/40 focus-within:border-white'
                    : 'bg-background border-input focus-within:ring-ring/40 focus-within:border-ring'
            ),

            policy: cn(
                'text-xs text-center',
                isPrimary ? 'text-white/80' : 'text-muted-foreground'
            ),

            // ✅ for the privacy policy link inside policy
            policyLink: cn(
                'font-medium underline underline-offset-4',
                isPrimary ? 'text-white' : 'text-foreground'
            ),
        };
    }, [variant, className]);

    const isHorizontal = layout === 'horizontal';

    return (
        <motion.div className={styles.wrapper} variants={fadeUp}>
            {/* subtle glow */}
            <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[28px] bg-gradient-to-r from-muted/40 to-muted/10 blur-2xl" />

            {/* header */}
            {contactHeader && (
                <div
                    className={cn(
                        'mb-6 space-y-2',
                        isHorizontal && 'lg:flex lg:items-end lg:justify-between'
                    )}
                >
                    <div>
                        <h3 className="fs-44 font-bold tracking-tight">{title}</h3>
                        <p className="text-sm text-muted-foreground">{subtitle}</p>
                    </div>
                </div>
            )}

            <motion.form
                onSubmit={handleSubmit}
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                className={cn(
                    // ✅ vertical keeps your spacing
                    !isHorizontal && 'space-y-5',
                    // ✅ horizontal uses grid so it can align in one row on desktop
                    isHorizontal && 'grid gap-5 lg:grid-cols-12 lg:items-end'
                )}
            >
                {/* NAME */}
                <motion.div
                    variants={fadeUp}
                    className={cn('space-y-2', isHorizontal ? 'lg:col-span-4' : '')}
                >
                    {isLabel && (
                        <div className="flex items-center justify-between">
                            <label className={styles.label}>Name</label>
                            <span className={styles.hint}>Required</span>
                        </div>
                    )}

                    <Input
                        type="text"
                        name="name"
                        placeholder="John Doe"
                        value={form.name}
                        className={styles.field}
                        onChange={handleChange}
                    />
                </motion.div>

                {/* EMAIL */}
                <motion.div
                    variants={fadeUp}
                    className={cn('space-y-2', isHorizontal ? 'lg:col-span-4' : '')}
                >
                    {isLabel && (
                        <div className="flex items-center justify-between">
                            <label className={styles.label}>Email</label>
                            <span className={styles.hint}>Required</span>
                        </div>
                    )}
                    <Input
                        type="email"
                        name="email"
                        placeholder="john@email.com"
                        value={form.email}
                        className={styles.field}
                        onChange={handleChange}
                    />
                </motion.div>

                {/* PHONE */}
                <motion.div
                    variants={fadeUp}
                    className={cn('space-y-2', isHorizontal ? 'lg:col-span-4' : '')}
                >
                    {isLabel && (
                        <div className="flex items-center justify-between">
                            <label className={styles.label}>Phone</label>
                            <span className={styles.hint}>Required</span>
                        </div>
                    )}
                    <div className={styles.phoneWrap}>
                        <PhoneInput
                            international
                            name="phone"
                            defaultCountry="US"
                            placeholder="(555) 000-0000"
                            value={form.phone}
                            onChange={handlePhoneChange}
                            className="w-full h-9 text-foreground"
                        />
                    </div>

                    {!isHorizontal && (
                        <p className={styles.hint}>
                            We’ll only use this to contact you about your request.
                        </p>
                    )}
                </motion.div>

                {/* SERVICES (full width in both layouts) */}
                <motion.div
                    variants={fadeUp}
                    className={cn('space-y-2', isHorizontal && 'lg:col-span-12')}
                >
                    {isLabel && (
                        <div className="flex items-center justify-between">
                            <label className={styles.label}>Services Interested In</label>
                            <span className={styles.hint}>Optional</span>
                        </div>
                    )}
                    <MultiSelect
                        options={serviceOptions}
                        value={form.services}
                        onChange={handleServicesChange}
                        placeholder="Select services you're interested in..."
                        className={styles.field + ' px-0'}
                    />
                </motion.div>

                {/* MESSAGE (full width in horizontal) */}
                <motion.div
                    variants={fadeUp}
                    className={cn('space-y-2', isHorizontal && 'lg:col-span-12')}
                >
                    {isLabel && (
                        <div className="flex items-center justify-between">
                            <label className={styles.label}>Message</label>
                            <span className={styles.hint}>Required</span>
                        </div>
                    )}
                    <Textarea
                        name="message"
                        placeholder="Tell us what you're trying to publish, timeline, and any goals..."
                        value={form.message}
                        className={styles.textarea}
                        onChange={handleChange}
                    />
                </motion.div>

                {/* BUTTON (inline in horizontal) */}
                {isHorizontal && (
                    <motion.div variants={fadeUp} className="lg:col-span-3">
                        <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                            <Button
                                type="submit"
                                className="w-full"
                                size="xl"
                                rounded="full"
                                disabled={loading}
                            >
                                {loading ? 'Sending...' : 'Send Message'}
                            </Button>
                        </motion.div>
                    </motion.div>
                )}

                {/* BUTTON (bottom in vertical) */}
                {!isHorizontal && (
                    <motion.div variants={fadeUp} className="pt-2">
                        <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                            <Button
                                type="submit"
                                className="w-full"
                                size="xl"
                                rounded="full"
                                disabled={loading}
                            >
                                {loading ? 'Sending...' : 'Send Message'}
                            </Button>
                        </motion.div>
                    </motion.div>
                )}

                {/* POLICY */}
                <motion.p
                    variants={fadeUp}
                    className={cn(styles.policy, isHorizontal && 'lg:col-span-12 text-left')}
                >
                    By submitting this form you agree to our{' '}
                    <a
                        href="/promotion-lp/privacy"
                        className="font-medium text-foreground underline underline-offset-4"
                    >
                        Privacy Policy
                    </a>
                    .
                </motion.p>
            </motion.form>
        </motion.div>
    );
}
